import { useWatch } from 'react-hook-form';
import React, { useMemo } from 'react';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';
import { FormFieldActions, FormFieldState } from '../../../hooks';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { SelectedCellInfo, useThuePaste } from '../hooks';
import { getTableColumns } from './columns';
import { FormMode } from '@/types/form';

interface ThueTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  state: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
  onRowClick?: (params: MuaHangRecord) => void;
  onCellClick?: (params: DataTableRenderCellParams<{ uuid?: string | null }>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onCellCommit?: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  onHandleDetail?: () => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

export const ThueTab: React.FC<ThueTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  state,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onCellCommit,
  onHandleDetail,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const [dien_giai, so_ct0, so_ct2] = useWatch({ name: ['dien_giai', 'so_ct0', 'so_ct2'] });

  const columns = useMemo(
    () => getTableColumns(formMode, state, dien_giai, so_ct0, so_ct2, onCellValueChange, onCellCommit),
    [formMode, state, dien_giai, so_ct0, so_ct2, onCellValueChange, onCellCommit]
  );

  useThuePaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  return (
    <ErpInputTableV2
      rows={rows}
      autoHeight
      showIndexColumn
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns as MuaHangRecord[]}
      getRowId={row => row?.uuid || ''}
      pasteTarget={selectedCell}
      actionLoading={isPasting}
      actionsDisabled={formMode === 'view'}
      actionOrder={['detail', 'add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin']}
      actions={{
        detail: onHandleDetail || (() => console.log('Detail clicked')),
        add: onAddRow,
        delete: onDeleteRow,
        copy: onCopyRow,
        paste: onPasteRow,
        moveUp: () => onMoveRow('up'),
        moveDown: () => onMoveRow('down'),
        export: () => console.log('Export clicked'),
        pin: () => console.log('Pin clicked')
      }}
    />
  );
};
